import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import {
  STATUS_LABEL,
  STATUS_LABEL_KEY,
  useLocations,
  type LocationStatus,
} from '@/hooks/use-locations';
import { useT } from '@/i18n/use-t';
import { cn } from '@/lib/utils';

const FILTERS: { value: 'all' | LocationStatus; labelKey: string; label: string }[] = [
  { value: 'active', labelKey: 'locations.list.filterActive', label: 'Active' },
  { value: 'all', labelKey: 'locations.list.filterAll', label: 'All' },
  { value: 'archived', labelKey: 'locations.list.filterArchived', label: 'Archived' },
];

export function LocationsListPage() {
  const t = useT('admin_ui');
  const [filter, setFilter] = useState<'all' | LocationStatus>('active');
  const { data, isLoading } = useLocations({
    status: filter === 'all' ? undefined : [filter],
  });
  const items = data?.items ?? [];

  const newLocation = t('locations.new.title', 'New location');
  const [emptyBefore, emptyAfter] = t(
    'locations.list.empty',
    'No locations yet. Click {action} to add the first one.',
  ).split('{action}');

  return (
    <div className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('locations.list.title', 'Locations')}</h1>
          <p className="text-sm text-muted-foreground">
            {t(
              'locations.list.description',
              'Physical stores. Single-location deployments can ignore this — multi-location chains create one row per store and bind terminals + cashiers to it.',
            )}
          </p>
        </div>
        <Button asChild>
          <Link to="/locations/new">{newLocation}</Link>
        </Button>
      </header>

      <div className="flex items-center gap-1 rounded-md border border-border bg-card p-1 text-sm">
        {FILTERS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className={cn(
              'rounded-sm px-3 py-1.5 transition-colors',
              filter === opt.value
                ? 'bg-secondary font-medium text-secondary-foreground'
                : 'text-muted-foreground hover:bg-secondary/60',
            )}
            onClick={() => setFilter(opt.value)}
          >
            {t(opt.labelKey, opt.label)}
          </button>
        ))}
      </div>

      <div className="rounded-md border border-border bg-card">
        {isLoading ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">
            {t('common.loading', 'Loading…')}
          </p>
        ) : items.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">
            {emptyBefore}
            <strong>{newLocation}</strong>
            {emptyAfter}
          </p>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>{t('locations.field.code', 'Code')}</TH>
                <TH>{t('locations.field.name', 'Name')}</TH>
                <TH>{t('locations.field.city', 'City')}</TH>
                <TH>{t('locations.field.country', 'Country')}</TH>
                <TH>{t('locations.field.status', 'Status')}</TH>
                <TH className="text-right">{t('locations.list.actions', 'Actions')}</TH>
              </TR>
            </THead>
            <TBody>
              {items.map((loc) => (
                <TR key={loc.id}>
                  <TD className="font-mono text-xs">{loc.code}</TD>
                  <TD>{loc.name}</TD>
                  <TD>{loc.city ?? '—'}</TD>
                  <TD className="font-mono text-xs">{loc.countryCode ?? '—'}</TD>
                  <TD>
                    <Badge variant={loc.status === 'active' ? 'success' : 'destructive'}>
                      {t(STATUS_LABEL_KEY[loc.status], STATUS_LABEL[loc.status])}
                    </Badge>
                  </TD>
                  <TD className="text-right">
                    <Button asChild variant="ghost" size="sm">
                      <Link to={`/locations/${loc.id}`}>{t('locations.list.open', 'Open')}</Link>
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </div>
    </div>
  );
}
